import { useMemo } from 'react';
import {
  findCoding,
  findIdentifier,
  formatCodingToken,
} from '@lifeomic/fhir-constants';
import { unwrapBundle } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import { useDataset, useFHIRQuery } from '../../../../util/api';
import { getMyOmeRqId } from '../../../../util/myome';

const MYOME_REQUISITION_ROLE_SYSTEM =
  'http://lifeomic.com/fhir/genomic-ingestion-myome-requisition-role';
const MYOME_REQUISITION_STATUS_SYSTEM =
  'http://lifeomic.com/fhir/genomic-ingestion-myome-requisition-status';
const MYOME_REQUISITION_REASON_SYSTEM =
  'http://lifeomic.com/fhir/genomic-ingestion-myome-requisition-reason';
const ELATION_VENDOR_ID_SYSTEM =
  'http://lifeomic.com/fhir/extension/ehr-elation-vendor-id';

/**
 * patient-service only sorts by `period` when the query also filters on it, so
 * the lower bound is far enough in the past to never exclude a Task.
 */
const ALL_PERIODS = 'ge2000-01-01';

/**
 * The patient's current MyOme original requisition, as projected by
 * genomic-ingestion-service. `statusReason.text` is support-only and is
 * deliberately not exposed.
 */
export type MyOmeRequisition = {
  taskId: string;
  /** MyOme's RQ id. Absent until MyOme accepts the order. Treat as opaque. */
  rqId: string | undefined;
  status: fhir3.Task['status'];
  /** The exact GIS requisition status, such as `AWAITING_GATES` or `OPEN`. */
  businessStatus: string | undefined;
  /** A stable GIS reason code, such as `consent-missing`. */
  reasonCode: string | undefined;
  elationLabOrderId: string | undefined;
};

export type MyOmeRequisitionResult =
  | { state: 'loading' }
  | { state: 'none' }
  | { state: 'error'; error: unknown; retry: () => void }
  | { state: 'found'; requisition: MyOmeRequisition };

const toMyOmeRequisition = (task: fhir3.Task): MyOmeRequisition => ({
  taskId: task.id ?? '',
  rqId: getMyOmeRqId(task),
  status: task.status,
  businessStatus: findCoding(task.businessStatus?.coding, {
    system: MYOME_REQUISITION_STATUS_SYSTEM,
  })?.code,
  reasonCode: findCoding(task.statusReason?.coding, {
    system: MYOME_REQUISITION_REASON_SYSTEM,
  })?.code,
  elationLabOrderId: findIdentifier(task.identifier, {
    system: ELATION_VENDOR_ID_SYSTEM,
  })?.value,
});

/**
 * Finds the patient's latest `sample-collection` requisition Task, whatever its
 * status. The specimen for an on-site blood draw ships against this one.
 */
export const useMyOmeRequisition = (
  patientId: string,
): MyOmeRequisitionResult => {
  const dataset = useDataset();
  const enabled = Boolean(patientId) && Boolean(dataset.id);

  const query = useFHIRQuery(
    'GET /v1/fhir/dstu3/Task',
    {
      _tag: dataset.codingToken,
      patient: patientId,
      code: formatCodingToken({
        system: MYOME_REQUISITION_ROLE_SYSTEM,
        code: 'sample-collection',
      }),
      period: ALL_PERIODS,
      _sort: '-period',
    },
    // A clinician waiting on MyOme returns to the member expecting the RQ id,
    // so don't serve a cached requisition without checking for a newer one.
    { enabled, staleTime: 0 },
  );

  const { data, isError, isFetching, error, refetch } = query;

  return useMemo((): MyOmeRequisitionResult => {
    if (!enabled) {
      return { state: 'none' };
    }
    if (data) {
      const current = unwrapBundle(data).find(
        task => task.status !== 'entered-in-error',
      );
      return current
        ? { state: 'found', requisition: toMyOmeRequisition(current) }
        : { state: 'none' };
    }
    if (isError && !isFetching) {
      return { state: 'error', error, retry: () => void refetch() };
    }
    return { state: 'loading' };
  }, [enabled, data, isError, isFetching, error, refetch]);
};
